import 'moment/locale/fr';
import './admin.css';

import {
  faArrowDownWideShort,
  faArrowUpWideShort,
  faPaperPlane,
} from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import useSendInvoicesSummary from '../../api/admin/useSendInvoicesSummary';
import useGetRequest from '../../api/useGetRequest';
import Datatable from '../../components/agv-datatable/index.js';
import GenericPageContainer from '../../components/agv-generic-page-container';
import HeaderButton from '../../components/agv-header-button';
import CustomDatePickerDashboard from '../../components/agv-header-date-picker';
import SearchFilter from '../../components/agv-header-search';
import SelectFilter from '../../components/agv-header-select';
import AgrivitechPagination from '../../components/agv-pagination';
import { ORDER_BY_ENUM } from '../../enums/orderby.enum';
import { buildUrlWithParams } from '../../utils/url.utils';
import SendSummaryModal from './send-summary-modal';

const Admin = () => {
  const navigate = useNavigate();

  const config = useSelector((state) => state.config);
  const isSuperAdmin = config?.is_super_admin;

  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  const {
    sendInvoicesSummary,
    isLoading: isSendingSummary,
    isSuccess: isSummarySent,
  } = useSendInvoicesSummary();

  useEffect(() => {
    if (isSummarySent) {
      setIsSendModalOpen(false);
    }
  }, [isSummarySent]);

  const [searchParams] = useSearchParams();
  const [filters, setFilters] = React.useState({
    limit: 20,
    offset: Number(searchParams.get('offset')) || 0,
    query: searchParams.get('query') || '',
    order:
      searchParams.get('order') === ORDER_BY_ENUM.ASC ||
      searchParams.get('order') === ORDER_BY_ENUM.DESC
        ? searchParams.get('order')
        : ORDER_BY_ENUM.DESC,
    ...(searchParams.get('start_date') &&
      !isNaN(new Date(searchParams.get('start_date'))) && {
        start_date: moment(new Date(searchParams.get('start_date'))).format(
          'YYYY-MM-DD'
        ),
      }),
    ...(searchParams.get('end_date') &&
      !isNaN(new Date(searchParams.get('end_date'))) && {
        end_date: moment(new Date(searchParams.get('end_date'))).format(
          'YYYY-MM-DD'
        ),
      }),
  });

  const onChangeDate = (dates) => {
    const [start, end] = dates;
    handleUpdateParam('start_date', start);
    handleUpdateParam('end_date', end);
  };

  /**
   * Handle the update of filters when a filter is changed
   * @param {string} key - The key of the filter
   * @param {string} value - The value of the filter
   * @returns {void}
   */
  const handleUpdateParam = useCallback(
    (key, value) => {
      setFilters((prevFilters) => {
        const newFilters = { ...prevFilters, [key]: value };

        if (key !== 'offset') {
          newFilters.offset = 0;
        }

        if (key === 'start_date' || key === 'end_date') {
          if (value) {
            newFilters[key] = moment(value).format('YYYY-MM-DD');
          } else {
            delete newFilters[key];
          }
        }

        if (
          key === 'status' &&
          (value === null || value === undefined || value === '')
        ) {
          delete newFilters.status;
        }

        return newFilters;
      });
    },
    [navigate]
  );

  const url = useMemo(
    () => buildUrlWithParams('/admin/invoices', filters),
    [filters]
  );

  const { data, isLoading, isFirstLoad } = useGetRequest(url, 300);

  useEffect(() => {
    const params = new URLSearchParams(filters);
    navigate({ search: params.toString() }, { replace: true });
  }, [filters, navigate]);

  return (
    <>
      <GenericPageContainer
        title={isSuperAdmin ? 'Factures (tous les clients)' : 'Mes Factures'}
        subtitle={
          isSuperAdmin
            ? "Consultez les factures de l'ensemble des clients Fleet"
            : "Consultez l'historique et le détail de vos factures Fleet"
        }
        filters={
          <>
            <SearchFilter
              value={filters.query}
              setValue={(value) => handleUpdateParam('query', value)}
              disabled={isLoading}
            />
            <div className="tw-relative">
              <ReactDatePicker
                selected={
                  filters.start_date ? new Date(filters.start_date) : null
                }
                onChange={onChangeDate}
                customInput={<CustomDatePickerDashboard />}
                // new Date si value car il faut une date et la valeur est "YYYY-MM-DD"
                startDate={
                  filters.start_date ? new Date(filters.start_date) : null
                }
                endDate={filters.end_date ? new Date(filters.end_date) : null}
                selectsRange
                locale="fr"
                dateFormat="dd MMM yyyy"
              />
            </div>
            <SelectFilter
              value={filters.order}
              onChange={(value) => handleUpdateParam('order', value)}
              options={[
                { value: ORDER_BY_ENUM.DESC, name: 'Desc.' },
                { value: ORDER_BY_ENUM.ASC, name: 'Asc.' },
              ]}
              icon={
                filters.order === ORDER_BY_ENUM.DESC
                  ? faArrowUpWideShort
                  : faArrowDownWideShort
              }
            />
            {isSuperAdmin && (
              <HeaderButton
                onClick={() => {
                  if (!isSendingSummary) {
                    setIsSendModalOpen(true);
                  }
                }}
                text={'Envoyer le récapitulatif'}
                icon={faPaperPlane}
                disabled={isSendingSummary}
              />
            )}
          </>
        }
      >
        <div
          className={`tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-slate-200 ${data?.pricing ? 'tw-overflow-hidden' : ''}`}
        >
          {data?.pricing && (
            <div className="tw-bg-slate-50 tw-border-b tw-border-slate-200 tw-p-3">
              <div className="tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-2">
                <span className="tw-text-sm tw-font-medium tw-text-slate-700">
                  Tarification :
                </span>
                <div className="tw-flex tw-items-center tw-gap-4 tw-text-sm">
                  <span className="tw-text-slate-600">
                    Véhicule:{' '}
                    <span className="tw-font-semibold tw-text-slate-900">
                      {data.pricing.price_per_vehicle != undefined
                        ? `${parseFloat(data.pricing.price_per_vehicle).toFixed(2)}€`
                        : 'N/A'}
                    </span>
                  </span>
                  <span className="tw-text-slate-600">
                    Email:{' '}
                    <span className="tw-font-semibold tw-text-blue-600">
                      {data.pricing.price_per_email != undefined
                        ? `${parseFloat(data.pricing.price_per_email).toFixed(2)}€`
                        : 'N/A'}
                    </span>
                  </span>
                  <span className="tw-text-slate-600">
                    SMS:{' '}
                    <span className="tw-font-semibold tw-text-green-600">
                      {data.pricing.price_per_sms != undefined
                        ? `${parseFloat(data.pricing.price_per_sms).toFixed(2)}€`
                        : 'N/A'}
                    </span>
                  </span>
                  <span className="tw-text-slate-600">
                    Signature:{' '}
                    <span className="tw-font-semibold tw-text-purple-600">
                      {data.pricing.price_per_signature != undefined
                        ? `${parseFloat(data.pricing.price_per_signature).toFixed(2)}€`
                        : 'N/A'}
                    </span>
                  </span>
                </div>
              </div>
            </div>
          )}
          <Datatable
            style={'tw-mt-0 tw-border-0'}
            isLoading={isLoading}
            isFirstLoading={isFirstLoad && isLoading}
            header={[
              ...(isSuperAdmin ? ['Client'] : []),
              'Période',
              'Véhicules actifs',
              'Emails envoyés',
              'SMS envoyés',
              'Signatures',
              'Montant HT',
              'Facture PDF',
            ]}
            data={data?.invoices || []}
            values={(element) => [
              ...(isSuperAdmin
                ? [
                    <span
                      key={`${element?.id}_client`}
                      className="tw-font-semibold tw-text-slate-900"
                    >
                      {element?.client_name || element?.client_id}
                    </span>,
                  ]
                : []),
              <span
                key={`${element?.id}_period`}
                className="tw-font-medium tw-text-slate-900"
              >
                {moment(element?.end_date).format('MMMM YYYY')}
              </span>,
              <span
                key={`${element?.id}_vehicles`}
                className="tw-font-medium tw-text-slate-700"
              >
                {element?.vehicle_count || 0}
              </span>,
              <span
                key={`${element?.id}_emails`}
                className="tw-font-medium tw-text-blue-600"
              >
                {element?.email_count || 0}
              </span>,
              <span
                key={`${element?.id}_sms`}
                className="tw-font-medium tw-text-green-600"
              >
                {element?.sms_count || 0}
              </span>,
              <span
                key={`${element?.id}_signatures`}
                className="tw-font-medium tw-text-purple-600"
              >
                {element?.signature_count || 0}
              </span>,
              <span
                key={`${element?.id}_total`}
                className="tw-font-bold tw-text-lg tw-text-slate-900"
              >
                {element?.total_price
                  ? `${parseFloat(element.total_price).toFixed(2)} € HT`
                  : '0.00 € HT'}
              </span>,
              <div
                key={`${element?.id}_invoice`}
                className="tw-flex tw-justify-center"
              >
                {element?.invoice_url ? (
                  <button
                    onClick={() => window.open(element.invoice_url, '_blank')}
                    className="tw-bg-green-600 hover:tw-bg-green-700 tw-text-white tw-px-3 tw-py-1.5 tw-rounded-md tw-text-sm tw-font-medium tw-transition-colors tw-flex tw-items-center tw-gap-2 tw-shadow-sm"
                    title="Télécharger la facture PDF"
                  >
                    <svg
                      className="tw-w-4 tw-h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    Télécharger
                  </button>
                ) : (
                  <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-1.5 tw-bg-amber-50 tw-border tw-border-amber-200 tw-rounded-md">
                    <svg
                      className="tw-w-4 tw-h-4 tw-text-amber-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span className="tw-text-amber-700 tw-text-sm tw-font-medium">
                      En préparation
                    </span>
                  </div>
                )}
              </div>,
            ]}
          />
        </div>

        {data?.pagination_info?.total_items > 1 && (
          <div className="tw-mt-4 tw-bg-slate-50 tw-rounded-lg tw-p-4 tw-border">
            <div className="tw-flex tw-justify-between tw-items-center">
              <span className="tw-font-medium tw-text-slate-700">
                Total sur {data.pagination_info.total_items} factures :
              </span>
              <span className="tw-font-bold tw-text-xl tw-text-slate-900">
                {parseFloat(data?.total_price || 0).toFixed(2)} € HT
              </span>
            </div>
          </div>
        )}

        {data?.pagination_info?.total_items > 0 && (
          <div className="tw-mt-6">
            <AgrivitechPagination
              data={data}
              setOffset={(value) => handleUpdateParam('offset', value)}
            />
          </div>
        )}
      </GenericPageContainer>

      {isSuperAdmin && (
        <SendSummaryModal
          isOpen={isSendModalOpen}
          setIsOpen={setIsSendModalOpen}
          sendInvoicesSummary={sendInvoicesSummary}
          isLoading={isSendingSummary}
        />
      )}
    </>
  );
};

export default Admin;
